import {
  faAngleLeft,
  faAngleRight,
  faArrowUpRightFromSquare,
  faBook,
  faCheck,
  faFlask,
  faPowerOff,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Copyable,
  Modal,
  RedirectUrlsInput,
  Select,
  Switch,
  TaskBar,
  Value,
  ValueList,
} from '.';
import theme from '../exports.module.scss';
import useIdentityGroups from '../hooks/identity-groups';
import {
  IdentityCallbackMode,
  IdentityGroup,
  IdentityGroupProvider,
  IdentityGroupProviderCheckResult,
} from '../types';
import classNames from '../utilities/class-names';
import {
  IdentityProviderInfo,
  jsonpadCallbackUrl,
  startTestSignIn,
} from '../utilities/identity-providers';
import './IdentityProviderSetupModal.scss';

const steps = [
  'Create an app',
  'Callback URL',
  'Credentials',
  'Test sign-in',
  'Go live',
] as const;

const callbackModeOptions: { label: string; value: IdentityCallbackMode }[] = [
  { label: 'Straight back to your app', value: 'app' },
  { label: 'Through JSONPad', value: 'jsonpad' },
];

type IdentityProviderSetupModalProps = {
  show: boolean;
  group: IdentityGroup;
  provider: IdentityProviderInfo;
  configuration: IdentityGroupProvider | null;
  initialStep?: number;
  onClose: () => void;

  /**
   * Called when the group or its provider settings change, so the page can
   * fetch them again
   */
  onChange: () => void;
};

/**
 * A step-by-step guide to setting up signing in with a provider for an
 * identity group
 */
export function IdentityProviderSetupModal({
  show,
  group,
  provider,
  configuration,
  initialStep = 0,
  onClose,
  onChange,
}: IdentityProviderSetupModalProps): ReactElement {
  const {
    pending,
    error,
    resetError,
    update: updateGroup,
    upsertProvider,
    checkProvider,
    startProviderTest,
  } = useIdentityGroups();

  const [step, setStep] = useState(initialStep);
  const [callbackMode, setCallbackMode] = useState<IdentityCallbackMode>(
    provider.defaultCallbackMode
  );
  const [redirectUrls, setRedirectUrls] = useState<string[]>([]);
  const [clientId, setClientId] = useState('');

  /**
   * The provider's other credentials, e.g. { clientSecret }, or Apple's team
   * id, key id and private key
   */
  const [fields, setFields] = useState<Record<string, string>>({});
  const [checkResult, setCheckResult] =
    useState<IdentityGroupProviderCheckResult | null>(null);

  // Start again each time the modal opens
  useEffect(() => {
    if (show) {
      setStep(initialStep);
      setCallbackMode(
        configuration?.callbackMode ?? provider.defaultCallbackMode
      );
      setRedirectUrls(group.redirectUrls);
      setClientId(configuration?.clientId ?? '');
      setFields(
        Object.fromEntries(
          provider.fields
            .filter(field => !field.secret)
            .map(field => [
              field.name,
              String(configuration?.config?.[field.name] ?? ''),
            ])
        )
      );
      setCheckResult(null);
      resetError();
    }
    // Only when the modal opens, not whenever the group is fetched again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleRedirectUrlsChange = useCallback(
    async (value: string[]) => {
      const saved = await updateGroup(group.id, { redirectUrls: value });

      if (saved) {
        setRedirectUrls(saved.redirectUrls);
        onChange();
      }
    },
    [updateGroup, group.id, onChange]
  );

  const handleCallbackModeChange = useCallback(
    async (mode: IdentityCallbackMode) => {
      setCallbackMode(mode);

      // A provider that isn't set up yet gets its callback mode when its
      // credentials are saved
      if (configuration && configuration.callbackMode !== mode) {
        if (
          await upsertProvider(group.id, provider.id, { callbackMode: mode })
        ) {
          onChange();
        }
      }
    },
    [configuration, upsertProvider, group.id, provider.id, onChange]
  );

  const handleSaveCredentials = useCallback(async () => {
    setCheckResult(null);

    const saved = await upsertProvider(group.id, provider.id, {
      clientId: clientId.trim(),
      ...Object.fromEntries(
        Object.entries(fields)
          .map(([name, value]) => [name, value.trim()])
          .filter(([, value]) => value)
      ),
      callbackMode,

      // A new provider stays disabled until it's been tested
      ...(configuration ? {} : { enabled: false }),
    });

    if (!saved) {
      return;
    }

    // Secrets are write-only, so don't keep them around after saving
    setFields(current =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([name]) => !provider.fields.find(f => f.name === name)?.secret
        )
      )
    );
    onChange();

    const result = await checkProvider(group.id, provider.id);

    if (result) {
      setCheckResult(result);
    }
  }, [
    upsertProvider,
    checkProvider,
    group.id,
    provider.id,
    clientId,
    fields,
    provider.fields,
    callbackMode,
    configuration,
    onChange,
  ]);

  const handleTest = useCallback(async () => {
    await startTestSignIn(group.id, provider.id, startProviderTest);
  }, [startProviderTest, group.id, provider.id]);

  const handleSetEnabled = useCallback(
    async (enabled: boolean) => {
      if (await upsertProvider(group.id, provider.id, { enabled })) {
        onChange();
      }
    },
    [upsertProvider, group.id, provider.id, onChange]
  );

  const credentialsComplete =
    !!clientId.trim() &&
    provider.fields.every(
      field =>
        field.optional ||
        !!fields[field.name]?.trim() ||
        (field.secret && !!configuration?.secretsSet) ||
        (!field.secret && !!configuration?.config?.[field.name])
    );

  const guideLink = (
    <Link to={provider.guide} target="_blank">
      <FontAwesomeIcon icon={faBook} /> Full {provider.name} setup guide
    </Link>
  );

  return (
    <Modal
      className="identity-provider-setup-modal"
      show={show}
      closable
      onClose={onClose}
    >
      <h2>
        <FontAwesomeIcon icon={provider.icon} /> Sign in with {provider.name}
        <span className="extra">{group.name || 'Default group'}</span>
      </h2>
      <ol className="identity-provider-setup-steps">
        {steps.map((label, i) => (
          <li
            key={label}
            className={classNames(
              i === step ? 'current' : '',
              i < step ? 'done' : ''
            )}
          >
            <button type="button" onClick={() => setStep(i)}>
              <span className="step-number">
                {i < step ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
              </span>{' '}
              {label}
            </button>
          </li>
        ))}
      </ol>
      <Alert intent="danger" show={!!error} closable onClose={resetError}>
        <p>{error?.message}</p>
      </Alert>

      {step === 0 && (
        <div className="identity-provider-setup-step">
          <p>
            Signing in with {provider.name} needs an OAuth app in your own{' '}
            {provider.name} account. It takes a few minutes.
          </p>
          <ol>
            {provider.createSteps.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
          <p>
            <a href={provider.consoleUrl} target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Open the{' '}
              {provider.consoleName}
            </a>
            <br />
            {guideLink}
          </p>
        </div>
      )}

      {step === 1 && (
        <div className="identity-provider-setup-step">
          <ValueList fullWidth>
            <Value
              label="Where does the provider send people?"
              htmlFor="callback-mode-input"
              value={
                <>
                  <Select
                    inputId="callback-mode-input"
                    options={callbackModeOptions.filter(
                      option =>
                        !provider.callbackModes ||
                        provider.callbackModes.includes(option.value)
                    )}
                    value={callbackModeOptions.find(
                      option => option.value === callbackMode
                    )}
                    disabled={pending || provider.callbackModes?.length === 1}
                    onChange={selected =>
                      selected &&
                      handleCallbackModeChange((selected as any).value)
                    }
                  />
                  <span className="extra">
                    {provider.callbackModeAdvice[callbackMode]}
                  </span>
                </>
              }
            />
          </ValueList>
          {callbackMode === 'jsonpad' ? (
            <>
              <h3>Callback URL for {provider.name}</h3>
              <p>{provider.callbackNotes.jsonpad}</p>
              <p>
                <Copyable text={jsonpadCallbackUrl} />
              </p>
              <h3>Your app's return pages</h3>
              <p>
                After signing in, JSONPad sends people to the page in your app
                that started the sign-in. Add each of those pages here (they
                call <code>completeIdentityOAuth()</code>). Localhost is fine
                for development.
              </p>
              <RedirectUrlsInput
                value={redirectUrls}
                onChange={handleRedirectUrlsChange}
                disabled={pending}
              />
            </>
          ) : (
            <>
              <h3>Your app's return pages</h3>
              <p>
                Add each page in your app that people return to after signing in
                (they call <code>completeIdentityOAuth()</code>). Localhost is
                fine for development.
              </p>
              <p>{provider.callbackNotes.app}</p>
              <RedirectUrlsInput
                value={redirectUrls}
                onChange={handleRedirectUrlsChange}
                disabled={pending}
                copyable
              />
              <p className="extra">
                To use <strong>Test sign-in</strong> in this mode, also add{' '}
                <Copyable text={jsonpadCallbackUrl} /> to {provider.name}. Test
                sign-ins always go through JSONPad, because they return to this
                dashboard.
              </p>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="identity-provider-setup-step">
          <p>
            Copy these from your {provider.name} OAuth app. The secret is
            encrypted when it's saved, and can't be seen again.
          </p>
          <ValueList fullWidth>
            <Value
              label={provider.clientIdLabel}
              htmlFor="client-id-input"
              value={
                <input
                  type="text"
                  id="client-id-input"
                  value={clientId}
                  autoComplete="off"
                  disabled={pending}
                  onChange={e => setClientId(e.target.value)}
                />
              }
            />
            {provider.fields.map(field => (
              <Value
                key={field.name}
                label={field.label}
                labelDescription={
                  field.secret && configuration?.secretsSet
                    ? '(leave empty to keep it)'
                    : field.optional
                      ? '(optional)'
                      : ''
                }
                htmlFor={`${field.name}-input`}
                value={
                  <>
                    {field.file ? (
                      <textarea
                        id={`${field.name}-input`}
                        className="identity-provider-setup-key"
                        value={fields[field.name] ?? ''}
                        rows={4}
                        spellCheck={false}
                        placeholder="-----BEGIN PRIVATE KEY-----"
                        disabled={pending}
                        onChange={e =>
                          setFields({
                            ...fields,
                            [field.name]: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <input
                        type={field.secret ? 'password' : 'text'}
                        id={`${field.name}-input`}
                        value={fields[field.name] ?? ''}
                        autoComplete={field.secret ? 'new-password' : 'off'}
                        placeholder={
                          field.secret && configuration?.secretsHint
                            ? `Ends in ${configuration.secretsHint}`
                            : ''
                        }
                        disabled={pending}
                        onChange={e =>
                          setFields({
                            ...fields,
                            [field.name]: e.target.value,
                          })
                        }
                      />
                    )}
                    {field.file && (
                      <input
                        type="file"
                        accept={field.file}
                        disabled={pending}
                        onChange={async e => {
                          const file = e.target.files?.[0];

                          if (file) {
                            setFields({
                              ...fields,
                              [field.name]: await file.text(),
                            });
                          }
                        }}
                      />
                    )}
                    {field.hint && <span className="extra">{field.hint}</span>}
                  </>
                }
              />
            ))}
          </ValueList>
          <TaskBar
            left={
              <Button
                intent="success"
                icon={faSave}
                label="Save and check"
                onClick={handleSaveCredentials}
                disabled={pending || !credentialsComplete}
                loading={pending}
              />
            }
            background={false}
          />
          <Alert
            intent={
              checkResult?.ok
                ? 'success'
                : checkResult?.ok === false
                  ? 'danger'
                  : 'warning'
            }
            show={!!checkResult}
          >
            <p>
              {checkResult?.ok
                ? `Saved. ${provider.name} accepted the client ID and secret.`
                : checkResult?.ok === false
                  ? `Saved, but ${provider.name} rejected the client ID or secret: ${checkResult.detail}`
                  : `Saved. ${checkResult?.detail}`}
            </p>
          </Alert>
        </div>
      )}

      {step === 3 && (
        <div className="identity-provider-setup-step">
          <p>
            Sign in with a real {provider.name} account to check that everything
            works. You'll come back to this dashboard and see what{' '}
            {provider.name} told JSONPad about the account. No identity is
            created or signed in.
          </p>
          {callbackMode === 'app' && (
            <p className="extra">
              Test sign-ins go through JSONPad's callback URL, so{' '}
              <Copyable text={jsonpadCallbackUrl} /> must be registered with{' '}
              {provider.name} as well.
            </p>
          )}
          {!configuration && (
            <Alert intent="warning" show>
              <p>Save your credentials first.</p>
            </Alert>
          )}
          <TaskBar
            left={
              <Button
                intent="info"
                icon={faFlask}
                label={`Test sign-in with ${provider.name}`}
                onClick={handleTest}
                disabled={pending || !configuration}
                loading={pending}
              />
            }
            background={false}
          />
        </div>
      )}

      {step === 4 && (
        <div className="identity-provider-setup-step">
          <ul>
            {provider.goingLiveSteps.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <p>{guideLink}</p>
          <h3>Turn it on</h3>
          <p>
            While it's disabled, apps can't sign in with {provider.name} for
            this group (but you can still test it).
          </p>
          <Switch
            name="provider-enabled"
            label="Enabled"
            icon={faPowerOff}
            color={theme.intentSuccess}
            value={!!configuration?.enabled}
            loading={pending}
            disabled={pending || !configuration}
            onChange={handleSetEnabled}
          />
          <h3>In your app</h3>
          <pre className="identity-provider-setup-code">
            {`// on the sign-in page
await jsonpad.startIdentityOAuth('${provider.id}', {
  redirectUrl: '${redirectUrls[0] ?? 'https://myapp.com/auth/callback'}',
});

// on the return page
const { identity, created } = await jsonpad.completeIdentityOAuth();`}
          </pre>
          <p>
            <Link to="/docs/identity-oauth" target="_blank">
              <FontAwesomeIcon icon={faBook} /> Signing in with OAuth
            </Link>
          </p>
        </div>
      )}

      <TaskBar
        className="identity-provider-setup-nav"
        left={
          step > 0 ? (
            <Button
              icon={faAngleLeft}
              label="Back"
              onClick={() => setStep(step - 1)}
              disabled={pending}
              secondary
            />
          ) : undefined
        }
        right={
          step < steps.length - 1 ? (
            <Button
              intent="info"
              icon={faAngleRight}
              label="Next"
              onClick={() => setStep(step + 1)}
              disabled={pending}
            />
          ) : (
            <Button
              intent="success"
              icon={faCheck}
              label="Done"
              onClick={onClose}
              disabled={pending}
            />
          )
        }
        background={false}
      />
    </Modal>
  );
}
